import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { CheckCircle2, HelpCircle, LogOut, Palette, RotateCcw, Settings, Sparkles, User } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useNotifications } from "../context/NotificationContext";
import { useThemeMode } from "../context/ThemeModeContext";
import ProtectedRoute from "../components/ProtectedRoute";
import { fetchCollegeData } from "../helpers/collegeAPI";
import { getAutocompleteSuggestions } from "../helpers/smartSearch";
import {
  CALM_ACCENT_PALETTES,
  FONT_PRESETS,
  getSchoolAccentSuggestion,
  getSchoolColorPreset,
  readCustomTheme,
  resetCustomTheme,
  saveCustomTheme,
} from "../helpers/customTheme";
import "./AccountPage.css";

const sections = [
  { id: "account", label: "Account", icon: User },
  { id: "settings", label: "Settings", icon: Settings },
  { id: "customization", label: "Customization", icon: Palette },
  { id: "support", label: "Support", icon: HelpCircle },
];

function normalizeSection(hash) {
  const section = hash.replace("#", "");
  return sections.some((item) => item.id === section) ? section : "account";
}

export default function AccountPage() {
  const { user, logout } = useAuth();
  const { notificationsEnabled, setNotificationsEnabled } = useNotifications();
  const { darkMode, setDarkMode } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeSection, setActiveSection] = useState(() => normalizeSection(location.hash));
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [collegeData, setCollegeData] = useState({ results: [] });
  const [collegeSuggestions, setCollegeSuggestions] = useState([]);
  const [schoolInputValue, setSchoolInputValue] = useState(() => readCustomTheme().school);
  const [schoolPickerOpen, setSchoolPickerOpen] = useState(false);
  const [collegeLoadError, setCollegeLoadError] = useState(false);
  const [customTheme, setCustomTheme] = useState(() => readCustomTheme());
  const schoolSearchDebounceRef = React.useRef(null);

  useEffect(() => {
    setActiveSection(normalizeSection(location.hash));
  }, [location.hash]);

  useEffect(() => {
    let cancelled = false;
    fetchCollegeData()
      .then((data) => {
        if (cancelled) return;
        setCollegeData(data?.results ? data : { results: [] });
      })
      .catch(() => {
        if (!cancelled) setCollegeLoadError(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (schoolSearchDebounceRef.current) clearTimeout(schoolSearchDebounceRef.current);

    const resultsArray = collegeData?.results ?? [];
    if (!schoolInputValue || resultsArray.length === 0) {
      setCollegeSuggestions([]);
      return;
    }

    schoolSearchDebounceRef.current = setTimeout(() => {
      setCollegeSuggestions(getAutocompleteSuggestions(schoolInputValue, resultsArray, 12));
    }, 150);

    return () => {
      if (schoolSearchDebounceRef.current) clearTimeout(schoolSearchDebounceRef.current);
    };
  }, [schoolInputValue, collegeData]);

  const creationDate = user?.metadata?.creationTime
    ? new Date(user.metadata.creationTime).toLocaleDateString()
    : "Unknown";

  const displayName = user?.displayName || "Not set";
  const email = user?.email || "—";
  const avatarInitial = (user?.displayName || user?.email || "M").trim().charAt(0).toUpperCase();

  const accountRows = useMemo(() => ([
    { label: "Email Address", value: email },
    { label: "Display Name", value: displayName },
    { label: "Account Created", value: creationDate },
  ]), [creationDate, displayName, email]);

  const handleSelectSection = (sectionId) => {
    setActiveSection(sectionId);
    navigate(`/account#${sectionId}`);
  };

  const handleSignOut = async () => {
    setError("");
    setLoading(true);
    try {
      await logout();
      navigate("/login");
    } catch (err) {
      setError("Failed to sign out. Please try again.");
      setLoading(false);
    }
  };

  const selectedPreset = getSchoolColorPreset(customTheme.school);
  const selectedSuggestion = getSchoolAccentSuggestion(customTheme.school);
  const schoolNoOptionsText = collegeLoadError
    ? "Colleges couldn't load. You can still choose a color."
    : schoolInputValue.trim()
      ? 'College not found'
      : 'Start typing to search colleges';

  const updateTheme = (changes) => {
    setCustomTheme((prev) => {
      const next = saveCustomTheme({ ...prev, ...changes });
      return next;
    });
  };

  const handleSchoolChange = (schoolName) => {
    const suggestion = getSchoolAccentSuggestion(schoolName);
    setSchoolInputValue(schoolName || "");
    setSchoolPickerOpen(false);
    setCollegeSuggestions([]);
    updateTheme({
      school: schoolName || "",
      accent: suggestion?.accent || customTheme.accent || CALM_ACCENT_PALETTES[0].accent,
      palette: suggestion ? suggestion.source : customTheme.palette === "preset" ? CALM_ACCENT_PALETTES[0].id : customTheme.palette,
    });
  };

  const handlePaletteChange = (palette) => {
    updateTheme({
      accent: palette.accent,
      palette: palette.id,
    });
  };

  const handlePresetAccentChange = (accent) => {
    updateTheme({
      accent,
      palette: "preset",
    });
  };

  const handleResetTheme = () => {
    resetCustomTheme();
    setCustomTheme(readCustomTheme());
    setSchoolInputValue("");
    setSchoolPickerOpen(false);
    setCollegeSuggestions([]);
  };

  return (
    <ProtectedRoute>
      <main className="account-page">
        <div className="account-page__inner">
          <header className="account-page__header">
            <div>
              <h1>Account</h1>
              <p>Manage your profile, app settings, and support options.</p>
            </div>
            <div className="account-page__user-pill">
              <span className="account-page__user-avatar">{avatarInitial}</span>
              <div>
                <strong>{user?.displayName || "My account"}</strong>
                <span>{email}</span>
              </div>
            </div>
          </header>

          {error && (
            <div className="account-page__error">
              {error}
            </div>
          )}

          <div className="account-page__layout">
            <aside className="account-page__nav" aria-label="Account sections">
              {sections.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  className={`account-page__nav-btn ${activeSection === id ? "account-page__nav-btn--active" : ""}`}
                  onClick={() => handleSelectSection(id)}
                >
                  <Icon size={16} strokeWidth={1.6} />
                  {label}
                </button>
              ))}
            </aside>

            <section className="account-page__panel">
              {activeSection === "account" && (
                <div className="account-page__section">
                  <div className="account-page__section-header">
                    <h2>Account Details</h2>
                    <p>Your sign-in information comes from your MaiColleges account.</p>
                  </div>

                  <div className="account-page__details">
                    {accountRows.map((row) => (
                      <div className="account-page__detail-row" key={row.label}>
                        <span>{row.label}</span>
                        <strong>{row.value}</strong>
                      </div>
                    ))}
                  </div>

                  <div className="account-page__muted-card">
                    <CheckCircle2 size={17} strokeWidth={1.6} />
                    <div>
                      <strong>Account active</strong>
                      <span>Your planning data is stored with this signed-in account in Firestore and cached on this device for faster loading.</span>
                    </div>
                  </div>
                </div>
              )}

              {activeSection === "settings" && (
                <div className="account-page__section">
                  <div className="account-page__section-header">
                    <h2>Settings</h2>
                    <p>Keep app preferences easy to scan and adjust.</p>
                  </div>

                  <div className="account-page__settings-list">
                    <div className="account-page__setting-row">
                      <div>
                        <strong>Appearance</strong>
                        <span>Choose light or dark mode. You can also use the sun and moon control in the top bar.</span>
                      </div>
                      <div className="account-page__choice-group" role="group" aria-label="Appearance">
                        <button
                          type="button"
                          className={!darkMode ? "account-page__choice-btn account-page__choice-btn--active" : "account-page__choice-btn"}
                          aria-pressed={!darkMode}
                          onClick={() => setDarkMode(false)}
                        >
                          Light
                        </button>
                        <button
                          type="button"
                          className={darkMode ? "account-page__choice-btn account-page__choice-btn--active" : "account-page__choice-btn"}
                          aria-pressed={darkMode}
                          onClick={() => setDarkMode(true)}
                        >
                          Dark
                        </button>
                      </div>
                    </div>
                    <div className="account-page__setting-row">
                      <div>
                        <strong>In-app reminders</strong>
                        <span>Show new task and application deadline alerts in MaiColleges. Existing notification history stays available.</span>
                      </div>
                      <button
                        type="button"
                        className={`account-page__switch ${notificationsEnabled ? "account-page__switch--on" : ""}`}
                        role="switch"
                        aria-checked={notificationsEnabled}
                        onClick={() => setNotificationsEnabled(!notificationsEnabled)}
                      >
                        <span aria-hidden="true" />
                        <strong>{notificationsEnabled ? "On" : "Off"}</strong>
                      </button>
                    </div>
                  </div>
                  <p className="account-page__settings-note">Reminder timing is selected on each task or General Progress step, so important dates can use different notice periods.</p>
                </div>
              )}

              {activeSection === "customization" && (
                <div className="account-page__section">
                  <div className="account-page__section-header">
                    <h2>Customization</h2>
                    <p>Personalize MaiColleges with a dream school accent and a font that feels right.</p>
                  </div>

                  <div className="account-page__custom-theme">
                    <div className="account-page__custom-theme-header">
                      <div>
                        <span className="account-page__section-kicker">
                          <Sparkles size={14} strokeWidth={1.7} />
                          Customization
                        </span>
                        <h3>Dream school accent</h3>
                        <p>Pick a school-inspired accent while keeping MaiColleges calm and easy to read.</p>
                      </div>
                      <button type="button" className="account-page__reset-theme-btn" onClick={handleResetTheme}>
                        <RotateCcw size={14} strokeWidth={1.7} />
                        Reset
                      </button>
                    </div>

                    <div className="account-page__theme-grid">
                      <label className="account-page__theme-field account-page__theme-field--wide">
                        <span>Dream school</span>
                        <Autocomplete
                          disablePortal
                          options={collegeSuggestions}
                          open={schoolPickerOpen && schoolInputValue.trim().length > 0 && collegeSuggestions.length > 0}
                          onClose={() => setSchoolPickerOpen(false)}
                          loading={schoolInputValue.trim().length > 0 && !collegeLoadError && collegeData.results.length === 0}
                          loadingText="Loading colleges..."
                          noOptionsText={schoolNoOptionsText}
                          inputValue={schoolInputValue}
                          value={customTheme.school || null}
                          onInputChange={(event, newInputValue) => {
                            setSchoolInputValue(newInputValue);
                            setSchoolPickerOpen(Boolean(newInputValue.trim()));
                            if (!newInputValue) {
                              setCollegeSuggestions([]);
                            }
                          }}
                          onChange={(event, newValue) => handleSchoolChange(newValue || "")}
                          filterOptions={(options) => options}
                          slotProps={{
                            paper: {
                              className: "account-page__theme-dropdown",
                              sx: {
                                borderRadius: "10px",
                                border: "1px solid var(--ds-border)",
                                boxShadow: "0 8px 30px rgba(0, 0, 0, 0.08)",
                                backgroundColor: "var(--ds-surface)",
                              },
                            },
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Search for a dream school"
                              size="small"
                              onFocus={() => {
                                if (schoolInputValue.trim()) setSchoolPickerOpen(true);
                              }}
                            />
                          )}
                        />
                      </label>

                      <label className="account-page__theme-field">
                        <span>Custom accent</span>
                        <div className="account-page__color-input-row">
                          <input
                            type="color"
                            value={customTheme.accent || CALM_ACCENT_PALETTES[0].accent}
                            onChange={(event) => updateTheme({ accent: event.target.value, palette: "custom" })}
                            aria-label="Choose custom accent color"
                          />
                          <code>{customTheme.accent || CALM_ACCENT_PALETTES[0].accent}</code>
                        </div>
                      </label>
                    </div>

                    {selectedSuggestion && (
                      <div className="account-page__theme-block">
                        <div className="account-page__theme-block-heading">
                          <strong>{selectedPreset ? "Suggested school colors" : "Generated school accent"}</strong>
                          <span>{selectedPreset ? "School-inspired, editable anytime" : "Created from this school name, editable anytime"}</span>
                        </div>
                        <div className="account-page__swatches">
                          {[selectedSuggestion.accent, selectedSuggestion.secondary].filter(Boolean).map((color) => (
                            <button
                              type="button"
                              key={color}
                              className={`account-page__swatch ${customTheme.accent === color ? "account-page__swatch--active" : ""}`}
                              style={{ backgroundColor: color }}
                              onClick={() => handlePresetAccentChange(color)}
                              aria-label={`Use accent ${color}`}
                              title={color}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="account-page__theme-block">
                      <div className="account-page__theme-block-heading">
                        <strong>Calm fallback palettes</strong>
                        <span>Good for schools without a preset</span>
                      </div>
                      <div className="account-page__palette-options">
                        {CALM_ACCENT_PALETTES.map((palette) => (
                          <button
                            type="button"
                            key={palette.id}
                            className={`account-page__palette-btn ${customTheme.palette === palette.id ? "account-page__palette-btn--active" : ""}`}
                            onClick={() => handlePaletteChange(palette)}
                          >
                            <span style={{ backgroundColor: palette.accent }} />
                            {palette.name}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="account-page__theme-block">
                      <div className="account-page__theme-block-heading">
                        <strong>Font</strong>
                        <span>Poppins stays the default</span>
                      </div>
                      <div className="account-page__font-options">
                        {FONT_PRESETS.map((font) => (
                          <button
                            type="button"
                            key={font.id}
                            className={`account-page__font-btn ${customTheme.font === font.id ? "account-page__font-btn--active" : ""}`}
                            onClick={() => updateTheme({ font: font.id })}
                            style={{ fontFamily: font.stack }}
                          >
                            <strong>{font.name}</strong>
                            <span>{font.description}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="account-page__theme-preview">
                      <div>
                        <strong>{customTheme.school || "MaiColleges"}</strong>
                        <span>{customTheme.accent ? "Custom accent active" : "Default theme active"}</span>
                      </div>
                      <button
                        type="button"
                        className={`account-page__background-toggle ${customTheme.background !== "none" ? "account-page__background-toggle--active" : ""}`}
                        onClick={() => updateTheme({ background: customTheme.background === "none" ? "calm" : "none" })}
                      >
                        Calm background {customTheme.background !== "none" ? "On" : "Off"}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {activeSection === "support" && (
                <div className="account-page__section">
                  <div className="account-page__section-header">
                    <h2>Support</h2>
                    <p>Get help with your account or college planning workflow.</p>
                  </div>

                  <div className="account-page__support-card">
                    <HelpCircle size={22} strokeWidth={1.6} />
                    <div>
                      <strong>Need help?</strong>
                      <span>Email support@maicolleges.com for account help, bug reports, or questions about your college planning workflow.</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="account-page__footer">
                <button
                  className="account-page__signout-btn"
                  onClick={handleSignOut}
                  disabled={loading}
                >
                  <LogOut size={15} strokeWidth={1.6} />
                  {loading ? "Signing out..." : "Sign Out"}
                </button>
              </div>
            </section>
          </div>
        </div>
      </main>
    </ProtectedRoute>
  );
}
